/* Серверный вход STEOS ID для общего листа BotCore.sid: разметку и анимации рисует шаблон, здесь только запросы и ответы. */
import { api, endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import type { Me } from '../../api/types';
import { applyRuntimePreferences } from '../settings/runtime-preferences';
import { uiText } from '../../i18n';

interface MergeIntent {
  operation_id: string;
  receipt: string;
}

export interface SidResult {
  linked: boolean;
  login: string | null;
  since: string | null;
  confirm?: boolean;
}

/** Ошибка для листа: text — строка под полями, taken — почта уже занята */
class SidError extends Error {
  constructor(
    readonly text: string,
    readonly taken = false,
  ) {
    super(text);
  }
}

const MERGE_KEY = 'steos:identity:merge';
const MERGE_WAIT_MS = 90_000;

function pendingMerge(): MergeIntent | null {
  try {
    return JSON.parse(sessionStorage.getItem(MERGE_KEY) || 'null');
  } catch {
    return null;
  }
}

/** Квитанция разрешает только статус переноса; пароль в хранилище не попадает */
function prepareMerge(): MergeIntent {
  const value = pendingMerge() ?? { operation_id: uuid(), receipt: uuid().replaceAll('-', '') + uuid().replaceAll('-', '') };
  sessionStorage.setItem(MERGE_KEY, JSON.stringify(value));
  return value;
}

const state = (me: Me): SidResult => ({ linked: me.steos_id.linked, login: me.steos_id.login, since: me.steos_id.since });

function publish(me: Me) {
  window.BotServerMe = me;
  document.dispatchEvent(new CustomEvent('steos:account-updated', { detail: me }));
}

async function refreshMe(): Promise<SidResult> {
  const me = await endpoints.me();
  publish(me);
  return state(me);
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/** Перенос в существующий STEOS ID меняет владельца сессии: после него сессия и настройки берутся заново */
async function finishMerge(intent: MergeIntent): Promise<SidResult> {
  const until = Date.now() + MERGE_WAIT_MS;
  while (Date.now() < until) {
    try {
      const result = await endpoints.account.mergeStatus(intent.operation_id, intent.receipt);
      if (result.status === 'completed') {
        api.reset();
        const session = await endpoints.session();
        applyRuntimePreferences(await endpoints.account.settings());
        ['steos:export:key', 'steos:export:operation', 'steos:person:pending', MERGE_KEY].forEach(key => sessionStorage.removeItem(key));
        publish(session.me);
        return state(session.me);
      }
    } catch (failure) {
      if (failure instanceof ApiError && failure.status === 404) {
        sessionStorage.removeItem(MERGE_KEY);
        return refreshMe();
      }
    }
    await sleep(3000);
  }
  throw new SidError(uiText('Перенос ещё не подтверждён. Проверка продолжится, откройте лист позже'));
}

function failureText(failure: unknown, mode: 'login' | 'register' | 'password-reset'): SidError {
  const code = failure instanceof ApiError ? failure.code : '';
  const status = failure instanceof ApiError ? failure.status : 0;
  if (code === 'email_taken') return new SidError(uiText('Почта уже занята'), true);
  if (code === 'account_provider_already_linked') return new SidError(uiText('Этот STEOS ID уже связан с другим Telegram'));
  if (code === 'account_merge_subscriptions_conflict') return new SidError(uiText('На обоих аккаунтах есть подписка. Для объединения напишите в поддержку'));
  if (status === 429) return new SidError(uiText('Слишком много попыток. Попробуйте позже'));
  if ([404, 501, 503].includes(status)) return new SidError(uiText('Подключение STEOS ID сейчас недоступно. Попробуйте позже'));
  if (mode === 'login') return new SidError(uiText('Неверная почта или пароль'));
  return new SidError(uiText(mode === 'register' ? 'Не удалось создать аккаунт. Попробуйте позже' : 'Не удалось отправить письмо'));
}

export const steosIdServer = {
  async go(mode: 'login' | 'register', body: { email: string; pass: string; agree: boolean }): Promise<SidResult> {
    const intent = mode === 'login' ? prepareMerge() : {};
    try {
      const result = await endpoints.account.identity(mode, {
        ...intent,
        email: body.email,
        password: body.pass,
        ...(mode === 'register' ? { consent: body.agree } : {}),
      });
      if (result.confirmation_required) return { linked: false, login: null, since: null, confirm: true };
      if (mode === 'login' && result.status && result.status !== 'completed') return finishMerge(intent as MergeIntent);
      sessionStorage.removeItem(MERGE_KEY);
      return refreshMe();
    } catch (failure) {
      if (mode === 'login' && failure instanceof ApiError && [400, 401, 409, 422, 429].includes(failure.status)) sessionStorage.removeItem(MERGE_KEY);
      throw failureText(failure, mode);
    }
  },
  async mail(email: string): Promise<void> {
    try {
      await endpoints.account.identity('password-reset', { email });
    } catch (failure) {
      throw failureText(failure, 'password-reset');
    }
  },
  check: refreshMe,
  /** Перенос, начатый до перезагрузки, доводится по сохранённой квитанции */
  resume(): void {
    const intent = pendingMerge();
    if (intent) void finishMerge(intent).then(result => window.BotCore.sid.sync(result), () => undefined);
  },
};
